/**
 * Cookie Consent —— 单一事实来源
 * --------------------------------------------------------------
 * 存储：localStorage['guineemall_cookie_consent']
 * 数据结构：
 *   {
 *     version: 1,
 *     timestamp: 1710000000000,
 *     categories: {
 *       necessary:  true,   // 必要（始终 true，不可关闭）
 *       functional: false,  // 功能
 *       analytics:  false,  // 统计分析
 *       marketing:  false,  // 营销/广告
 *     }
 *   }
 *
 * 事件：window 上派发 'cookie-consent-changed'（detail = 新 consent 对象）
 */

const STORAGE_KEY = 'guineemall_cookie_consent';
const CURRENT_VERSION = 1;
const EVENT_NAME = 'cookie-consent-changed';

export const COOKIE_CATEGORIES = {
    necessary: {
        key: 'necessary',
        label: '必要 Cookie',
        description:
            '保证网站基础功能运行所必需的 Cookie，无法关闭。没有它们，登录、购物车、安全校验等核心功能将无法正常工作。',
        required: true,
    },
    functional: {
        key: 'functional',
        label: '功能 Cookie',
        description:
            '让网站记住您的偏好设置（如语言、地区、登录状态），提供更个性化的使用体验。',
        required: false,
    },
    analytics: {
        key: 'analytics',
        label: '统计 Cookie',
        description:
            '帮助我们了解访客如何使用网站（页面访问量、停留时间等），用于改进产品与体验，不收集可识别个人身份的信息。',
        required: false,
    },
    marketing: {
        key: 'marketing',
        label: '营销 Cookie',
        description:
            '用于向您展示相关推广内容，也帮助我们衡量广告效果。禁用后您仍会看到广告，但不会与您的兴趣相关。',
        required: false,
    },
};

/** 分类 key 列表 */
export const CATEGORY_KEYS = Object.keys(COOKIE_CATEGORIES);

/** 读取 consent，未设置或版本过旧时返回 null（代表未决策） */
export function getConsent() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj || typeof obj !== 'object') return null;
        if ((obj.version ?? 0) !== CURRENT_VERSION) return null;
        return normalize(obj);
    } catch {
        return null;
    }
}

/** 已做出决策（不管接受/拒绝哪些非必要类） */
export function hasConsented() {
    return getConsent() !== null;
}

/** 某一分类是否已同意（necessary 永远 true；null/未同意则按默认 false 处理） */
export function hasCategoryConsent(key) {
    if (key === 'necessary') return true;
    const c = getConsent();
    return Boolean(c?.categories?.[key]);
}

/** 保存完整 consent 对象并派发事件 */
export function saveConsent(categories) {
    const normalized = normalize({ categories });
    if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
        window.dispatchEvent(
            new CustomEvent(EVENT_NAME, { detail: normalized })
        );
    }
    return normalized;
}

/** 全部接受 */
export function acceptAll() {
    return saveConsent({
        necessary: true,
        functional: true,
        analytics: true,
        marketing: true,
    });
}

/** 仅保留必要，拒绝其他所有 */
export function rejectAll() {
    return saveConsent({
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false,
    });
}

/** 重置（用于测试或用户想重新选择） */
export function resetConsent() {
    if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
    }
}

/** 订阅变化；返回取消订阅函数 */
export function subscribe(fn) {
    if (typeof window === 'undefined') return () => {};
    const handler = (e) => fn(e.detail);
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
}

// --- 内部 ---

function normalize({ categories }) {
    const c = categories || {};
    return {
        version: CURRENT_VERSION,
        timestamp: Date.now(),
        categories: {
            necessary: true, // 必要永远 true
            functional: Boolean(c.functional),
            analytics: Boolean(c.analytics),
            marketing: Boolean(c.marketing),
        },
    };
}
